import React, { useMemo } from "react";
import { Col } from "react-bootstrap";
import { Button, Flex, Table } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

import "./QcBulkUploadFormatPreview.css";

const FORMAT_PREVIEW_DUMMY_ROW_COUNT = 3;

const compactColumn = (title, dataIndex, width) => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  render: (cell) => cell || "\u00a0",
});

/** Column layout aligned with CustomerRangeSpecFile.xlsx */
const RANGES_FORMAT_COLUMNS = [
  compactColumn("Customer ID", "customerId", 96),
  compactColumn("Scope", "scope", 72),
  compactColumn("Sample Info", "sampleInfo", 100),
  {
    title: "Range Specs",
    key: "rangeSpecs",
    children: [
      compactColumn("Min Value", "minValue", 80),
      compactColumn("Max Value", "maxValue", 80),
      compactColumn("Unit", "unit", 56),
    ],
  },
  {
    title: "Regular Tests",
    key: "regularTests",
    children: [
      compactColumn("Match Scope", "matchScope", 100),
      compactColumn("Test Category", "testCategory", 100),
      compactColumn("Internal Name", "internalName", 100),
      compactColumn("Display Name", "displayName", 100),
    ],
  },
];

/** Column layout aligned with CustomerRulesSpecFile.xlsx */
const RULES_FORMAT_COLUMNS = [
  compactColumn("Customer ID", "customerId", 96),
  compactColumn("Scope", "scope", 72),
  compactColumn("QC Pointers", "qcPointers", 120),
  compactColumn("Specs", "specs", 72),
  compactColumn("Sample Type", "sampleType", 88),
  {
    title: "Regular Tests",
    key: "regularTests",
    children: [
      compactColumn("Match Scope", "matchScope", 100),
      compactColumn("Test Category", "testCategory", 100),
      compactColumn("Internal Name", "internalName", 100),
      compactColumn("Display Name", "displayName", 100),
    ],
  },
];

const getLeafDataIndexes = (columns) => columns.flatMap((column) => (
  column.children ? getLeafDataIndexes(column.children) : [column.dataIndex]
));

const buildFormatPreviewDummyRows = (columns, rowCount = FORMAT_PREVIEW_DUMMY_ROW_COUNT) => {
  const fields = getLeafDataIndexes(columns);
  return Array.from({ length: rowCount }, (_, index) => ({
    key: `preview-${index + 1}`,
    ...Object.fromEntries(fields.map((field) => [field, ""])),
  }));
};

const getFormatPreviewConfig = (uploadType) => {
  const columns = uploadType === "rules" ? RULES_FORMAT_COLUMNS : RANGES_FORMAT_COLUMNS;
  return {
    columns,
    dataSource: buildFormatPreviewDummyRows(columns),
  };
};

const buildSheetHeaderFromColumns = (columns) => {
  const leafCount = getLeafDataIndexes(columns).length;
  const headerRowTop = Array(leafCount).fill("");
  const headerRowSub = Array(leafCount).fill("");
  const merges = [];
  let columnIndex = 0;

  columns.forEach((column) => {
    if (column.children?.length) {
      const groupStart = columnIndex;
      headerRowTop[columnIndex] = column.title;

      column.children.forEach((child) => {
        headerRowSub[columnIndex] = child.title;
        columnIndex += 1;
      });

      if (column.children.length > 1) {
        merges.push({
          s: { r: 0, c: groupStart },
          e: { r: 0, c: columnIndex - 1 },
        });
      }
      return;
    }

    headerRowTop[columnIndex] = column.title;
    merges.push({
      s: { r: 0, c: columnIndex },
      e: { r: 1, c: columnIndex },
    });
    columnIndex += 1;
  });

  return {
    headerRows: [headerRowTop, headerRowSub],
    merges,
    leafCount,
  };
};

const downloadFormatExcel = (columns, filename) => {
  const { headerRows, merges, leafCount } = buildSheetHeaderFromColumns(columns);
  const emptyRows = Array.from(
    { length: FORMAT_PREVIEW_DUMMY_ROW_COUNT },
    () => Array(leafCount).fill(""),
  );
  const sheetData = [...headerRows, ...emptyRows];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  worksheet["!merges"] = merges;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, filename);
};

const downloadRangesExcel = () => {
  downloadFormatExcel(RANGES_FORMAT_COLUMNS, "CustomerRange_Example.xlsx");
};

const downloadRulesExcel = () => {
  downloadFormatExcel(RULES_FORMAT_COLUMNS, "CustomerRules_Example.xlsx");
};

export default function QcBulkUploadFormatPreview({ type }) {
  const formatPreview = useMemo(() => getFormatPreviewConfig(type), [type]);

  const handleDownloadExcel = () => {
    if (type === "ranges") {
      downloadRangesExcel();
    } else {
      downloadRulesExcel();
    }
  };

  return (
    <Col xs={12} lg={7} className="QcBulkUploadFormatPreview">
      <div className="QcBulkUploadFormatPreview__header">Example</div>
      <div className="QcBulkUploadFormatPreview__Container">
        <div className="QcBulkUploadFormatPreview__tableWrap">
          <Table
            className="QcBulkUploadFormatPreview__table"
            columns={formatPreview.columns}
            dataSource={formatPreview.dataSource}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: "max-content" }}
          />
        </div>
        <Flex justify="flex-end" className="QcBulkUploadFormatPreview__DownloadBtnContainer">
          <Button
            type="primary"
            onClick={handleDownloadExcel}
          >
            <DownloadOutlined />
          </Button>
        </Flex>
      </div>
    </Col>
  );
}
